import { useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { ulid } from 'ulid';
import ErrorPage from '@/components/shared/ErrorPage';
import { constants } from '@/constants';
import AppointmentInformationSkeletons from '@/features/confirmation/components/AppointmentInformationSkeletons';
import { useAppointmentInfoQuery } from '@/features/loadAppointment/api/getAppointmentInformation';
import {
  applyAppointmentHydration,
  buildAppointmentHydrationPayload,
  isCanceledAppointment,
  shouldHydrateAppointment,
} from '@/features/loadAppointment/utils/appointmentHydration';
import { persistEmailAppointmentId } from '@/features/loadAppointment/utils/emailAppointmentSession';
import useAppointmentStore from '@/store/AppointmentStore';
import useSessionStore from '@/store/SessionStore';

const redirectToSchedulerLanding = () => {
  window.location.replace(`${constants.APP_BASE_URL}`);
};

function LoadAppointment() {
  const [searchParams] = useSearchParams();
  const initialAppointmentIdRef = useRef(searchParams.get('appointmentId'));
  const appointmentId = initialAppointmentIdRef.current ?? '';

  const {
    setSelectedDestination,
    setSearchedDestination,
    setSelectedAgent,
    setAppointmentId,
    setSelectedAppointmentType,
    setContactInfo,
    setSelectedStartTime,
    setSelectedEndTime,
    setCustomerId,
    setConfirmationId,
    setConfirmedAppointment,
    setSelectedBranch,
  } = useAppointmentStore();

  const { setSessionSource, setSessionId, sessionId } = useSessionStore();

  const hydratedAppointmentIdRef = useRef<string | null>(null);

  const { data, isError, refetch } = useAppointmentInfoQuery(appointmentId);

  useEffect(() => {
    if (
      !data ||
      !shouldHydrateAppointment(
        hydratedAppointmentIdRef.current,
        data.appointmentId,
      )
    ) {
      return;
    }

    hydratedAppointmentIdRef.current = data.appointmentId;

    if (isCanceledAppointment(data.status)) {
      return;
    }

    persistEmailAppointmentId(data.appointmentId);

    applyAppointmentHydration(buildAppointmentHydrationPayload(data), {
      setSelectedBranch,
      setConfirmedAppointment,
      setSelectedDestination,
      setSearchedDestination,
      setSelectedAgent,
      setAppointmentId,
      setSelectedAppointmentType,
      setContactInfo,
      setSelectedStartTime,
      setSelectedEndTime,
      setConfirmationId,
      setCustomerId,
      setSessionSource,
    });

    if (!sessionId) {
      setSessionId(ulid());
    }

    window.location.replace(
      `${constants.APP_BASE_URL}/confirmation?source=email`,
    );
  }, [
    data,
    setSelectedBranch,
    setConfirmedAppointment,
    setSelectedDestination,
    setSearchedDestination,
    setSelectedAgent,
    setAppointmentId,
    setSelectedAppointmentType,
    setContactInfo,
    setSelectedStartTime,
    setSelectedEndTime,
    setConfirmationId,
    setCustomerId,
    setSessionSource,
    sessionId,
    setSessionId,
  ]);

  if (!appointmentId) {
    return (
      <ErrorPage
        onClickTryAgain={redirectToSchedulerLanding}
        message='Appointment ID is missing.'
        buttonLabel='Start over'
      />
    );
  }

  if (isError) {
    return <ErrorPage onClickTryAgain={refetch} />;
  }

  if (isCanceledAppointment(data?.status)) {
    return (
      <ErrorPage
        onClickTryAgain={redirectToSchedulerLanding}
        message={'This appointment has been cancelled.'}
        imageUrl='/travel/agent/scheduler/canceledAppointmentImage.png'
        buttonLabel='Start over'
      />
    );
  }

  return (
    <AppointmentInformationSkeletons
      isInBranch={data?.appointmentType === 'In Person'}
    />
  );
}
export default LoadAppointment;
